import { lazy, Suspense, useEffect, useState, type ReactNode } from "react";

// The real TooltipProvider from @radix-ui/react-tooltip is ~6 KB gzipped, but it
// pulls in the rest of the Radix tooltip primitive tree. We defer mounting it until
// after the first paint so the homepage's critical-path JS stays lean. Until it
// mounts, children render normally — tooltips just won't show for the first
// ~800ms, which is fine since they're decorative.
const RealTooltipProvider = lazy(() =>
  import("@/components/ui/tooltip").then((m) => ({
    default: m.TooltipProvider,
  })),
);

export function DeferredTooltipProvider({ children }: { children: ReactNode }) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const id = window.requestIdleCallback
      ? window.requestIdleCallback(() => setMounted(true), { timeout: 600 })
      : window.setTimeout(() => setMounted(true), 0);
    return () => {
      if (typeof id === "number") window.clearTimeout(id);
      else window.cancelIdleCallback(id as number);
    };
  }, []);

  if (!mounted) {
    // Render children without the tooltip context until the real provider mounts.
    return <>{children}</>;
  }
  return (
    <Suspense fallback={children}>
      <RealTooltipProvider>{children}</RealTooltipProvider>
    </Suspense>
  );
}
